import React from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import type { colors } from '../../../theme/axiom-theme';
import { ColorUtil } from '../../../utils/color-util';

/**
 * The palette `Layout` and `LayoutItem` take for their own `background`, plus `transparent` - which
 * `colors` has no entry for, and which a fade resolving to nothing needs.
 */
export type DockFadeColorType = keyof typeof colors | 'transparent';

export type DockProps = {
  /**
   * Whatever is being held at the edge - a composer, a bar of actions. It keeps its own surface and
   * its own handlers; nothing here paints over it.
   */
  children: React.ReactNode;
  /**
   * The color the fade resolves to, and **whether there is a fade at all**. Name the page's own
   * background, since a fade to anything else is a band of that color under the dock.
   */
  fadeColor?: DockFadeColorType;
  name?: string;
  /**
   * Which edge of the scrollport the dock sticks to; `top` reverses the fade with it.
   */
  position?: 'top' | 'bottom';
};

/**
 * Holds its children near an edge of the scrollport they are in, with the content scrolling behind
 * them fading out into the page.
 *
 * It is CSS stickiness, so **no ancestor between it and the scroller may clip on that axis**
 * (`overflow: hidden`), or there is nothing to stick to.
 *
 * The fade has to sit above the scrolling content and below the dock's children, at a size the
 * caller never states. Unrelated to `Sticky`, which is Semantic's scroll-listening JavaScript.
 */
export const Dock = ({
  children,
  fadeColor,
  name,
  position = 'bottom',
}: DockProps) => (
  <div
    className={AttrsHelper.formatClassname(
      'dock',
      `dock-${position}`,
      /*
        A class, not a resolved color: the LESS palette is the one a theme can retint, so a value
        from the TypeScript map would leave the fade behind. `transparent` is a keyword with no
        palette entry, so it is named here.
      */
      fadeColor === 'transparent'
        ? 'dock-fade-transparent'
        : ColorUtil.tokenClass({ color: fadeColor, prefix: 'dock-fade' })
    )}
    data-test={name}
  >
    {children}
    {/*
      A real element, not a `::before`, so not asking for a fade mounts nothing rather than turning
      one off.
    */}
    {fadeColor ? (
      <div className="dock-fade" data-test={name ? `${name}_FADE` : null} />
    ) : null}
  </div>
);
